import { useCallback, useEffect, useMemo, useRef } from "react"
import { List, ListItemButton, useTheme } from "@mui/material"
import { Splide, SplideSlide } from "@splidejs/react-splide"
import "@splidejs/react-splide/css"
import { first } from "lodash"

export const OrdinalSelectSplide = (props) => {
  const {
    levels = [],
    onChangeOrdinal = () => {},
    selectedOrdinal = 0,
    syncWithActiveSlide = true,
    options = {},
  } = props
  const splideRef = useRef(null)
  const theme = useTheme()
  const styleOverrides =
    theme.components[`OrdinalSelectSplide`]?.styleOverrides || {}
  const { listItemButton, root } = styleOverrides

  const selectedLevelIndex = useMemo(
    () =>
      levels.findIndex(
        (level) => level.properties?.ordinal === selectedOrdinal
      ),
    [levels, selectedOrdinal]
  )

  /**
   * NOTE: for prevent onActive trigger when levels is changed
   * This is because the splide is not re-rendering when the levels change.
   * This is a workaround to force the splide to re-render when the levels change.
   */
  const splideKey = useMemo(() => `${first(levels)?.id}`, [levels])

  const handleDragging = useCallback((splide) => {
    const slides = splide.root.querySelectorAll(" .splide__slide")
    slides.forEach((slide) => {
      slide.classList.add("is-dragging")
    })
  }, [])

  const handleDragged = useCallback((splide) => {
    const slides = splide.root.querySelectorAll(" .splide__slide")
    slides.forEach((slide) => {
      slide.classList.remove("is-dragging")
    })
  }, [])

  const handleChangeActiveSlide = useCallback(
    (_splide, slideData) => {
      if (!syncWithActiveSlide) return
      onChangeOrdinal(Number(slideData.slide.dataset.ordinal))
    },
    [onChangeOrdinal, syncWithActiveSlide]
  )

  const handleClickSlide = useCallback(
    (_splide, slideData) => {
      onChangeOrdinal(Number(slideData.slide.dataset.ordinal))
    },
    [onChangeOrdinal]
  )

  const handleOnReady = useCallback(
    (splide) => {
      /**
       * Workaround to center the active slide after page refresh.
       */
      setTimeout(() => {
        splide.go(selectedLevelIndex + 1)
        splide.go(selectedLevelIndex)
      }, 250)
    },
    [selectedLevelIndex]
  )

  const handleOrdinalChange = useCallback((index) => {
    const splide = splideRef.current?.splide
    if (splide) {
      splide.go(index)
    }
  }, [])

  useEffect(() => {
    handleOrdinalChange(selectedLevelIndex)
  }, [selectedLevelIndex, handleOrdinalChange])

  if (levels.length <= 0) return null

  return (
    <List sx={root}>
      <Splide
        key={splideKey}
        options={{
          type: "slide",
          perPage: 5,
          pagination: false,
          arrows: false,
          focus: "center",
          drag: "free",
          snap: true,
          trimSpace: false,
          direction: "ttb",
          height: "340px",
          updateOnMove: true,
          padding: 0,
          isNavigation: true,
          start: selectedLevelIndex >= 0 ? selectedLevelIndex : 0,
          ...options,
        }}
        aria-label="Slide"
        onDrag={handleDragging}
        onActive={handleChangeActiveSlide}
        onDragged={handleDragged}
        onClick={handleClickSlide}
        onReady={handleOnReady}
        ref={splideRef}
      >
        {levels.map((level) => {
          return (
            <SplideSlide
              key={`ordinal-selector-${level.id}`}
              data-ordinal={level.properties.ordinal}
            >
              <ListItemButton
                sx={listItemButton}
                selected={level.properties?.ordinal === selectedOrdinal}
              >
                {level.properties.name?.en}
              </ListItemButton>
            </SplideSlide>
          )
        })}
      </Splide>
    </List>
  )
}
